import { useCreateAnalysis } from '@modules/analysis/hooks/useCreateAnalysis';
import { useUpdateAnalysis } from '@modules/analysis/hooks/useUpdateAnalysis';
import { EscHotKeyIcon } from '@modules/common/components/HotKeyIcons';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { useHotkey } from '@modules/common/hooks';
import { LIST_EQL_FIELDS_GQL } from '@modules/common/hooks/useFetchEQLFields';
import { FETCH_QUERY_BUILDER_GROUPS_GQL } from '@modules/common/hooks/useFetchQueryBuilderGroupOptions/constants';
import { isEmptyWhereClause } from '@modules/common/utils';
import { trackCustomFilterEvents } from '@modules/queryBuilder/helpers';
import { useQueryBuilderStore } from '@modules/queryBuilder/store/useQueryBuilderStore';
import { FilterListRounded, KeyboardReturnRounded } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Stack, Tooltip } from '@mui/material';
import { AnalysisUseCase, Status } from 'generated/graphql';
import { SyntheticEvent, useEffect, useState } from 'react';
import { Scope } from 'v2/core/entities/scope';
import { useGetEQLExpression } from 'v2/core/repository/edge-server/eql/useGetEQLExpression';
import { ExpressionType } from 'v2/core/repository/eql/types';
import { useGetUser } from 'v2/core/services/user';
import { CmdHotKeyIcon } from '../HotKeyIcons/CmdHotKeyIcon';

interface CustomFiltersModalActionsProps {
  onClose: () => void;
  filterName: string | undefined;
  showDeleteButton?: boolean;
  primaryAction?: 'EDIT' | 'SAVE';
  analysisID?: string;
  analysisEditToken?: string;
  isDisabled?: boolean;
}

export const CustomFiltersModalActions = ({
  onClose,
  filterName,
  showDeleteButton = false,
  primaryAction = 'SAVE',
  analysisID,
  analysisEditToken,
  isDisabled,
}: CustomFiltersModalActionsProps) => {
  const whereClause = useQueryBuilderStore(
    ({ metadata }) => metadata.whereClause,
  );

  const [initialFilterName] = useState(filterName);
  const [initialEql, setInitialEQL] = useState<string>();
  const [isDeleteInProgress, setIsDeleteInProgress] = useState(false);

  const hasIncompleteFilters = useQueryBuilderStore(
    ({ metadata }) => metadata.hasIncompleteFilters,
  );

  const {
    tenant: { id: tenantID },
  } = useGetUser();

  const { showSuccessNotification } = useNotificationContext();

  const { eql } = useGetEQLExpression({
    tenantID,
    eqlProps: {
      whereClause,
      expressionType: ExpressionType.Records,
    },
  });

  useEffect(() => {
    if (isEmptyWhereClause(whereClause)) {
      return;
    }

    if (!initialEql) {
      setInitialEQL(eql);
    }
  }, [eql, initialEql, whereClause]);

  const { createAnalysis, isLoading: isCreateInProgress } = useCreateAnalysis({
    useCase: AnalysisUseCase.CustomFilter,
    name: filterName ?? 'Custom Filter',
    scope: Scope.PUBLIC_EDITABLE,
    eqlProps: {
      whereClause,
      expressionType: ExpressionType.Records,
    },
    refetchQueries: [FETCH_QUERY_BUILDER_GROUPS_GQL, LIST_EQL_FIELDS_GQL],
  });

  const { updateAnalysis, isLoading: isUpdateInProgress } = useUpdateAnalysis(
    {
      title: filterName,
      scope: Scope.PUBLIC_EDITABLE,
      id: analysisID,
      context: {
        eqlExpression: eql ?? '',
      },
      editToken: analysisEditToken ?? '',
      useCase: AnalysisUseCase.CustomFilter,
    },
    true,
    [FETCH_QUERY_BUILDER_GROUPS_GQL, LIST_EQL_FIELDS_GQL],
  );

  const onSave = async (event?: SyntheticEvent | KeyboardEvent) => {
    event?.stopPropagation();

    try {
      if (primaryAction === 'SAVE') {
        await createAnalysis({});
        onClose();
        showSuccessNotification({
          contentComponent: `Custom Filter "${filterName}" saved`,
          props: {
            icon: <FilterListRounded />,
          },
        });
        trackCustomFilterEvents({
          eventName: 'Create Custom Filter Submitted',
          filterName: filterName ?? '',
          eql: eql ?? '',
        });
      }

      if (primaryAction === 'EDIT') {
        await updateAnalysis({});
        onClose();
        showSuccessNotification({
          contentComponent: `Custom Filter "${filterName}" updated`,
          props: {
            icon: <FilterListRounded />,
          },
        });

        trackCustomFilterEvents({
          eventName: 'Edit Custom Filter Submitted',
          filterName: filterName ?? '',
          eql: eql ?? '',
          initialEql: initialEql ?? '',
          initialFilterName: initialFilterName ?? '',
        });
      }
    } catch {}
  };

  const isLoading =
    isCreateInProgress || isUpdateInProgress || isDeleteInProgress;

  useHotkey({
    hotkey: '$mod+enter',
    onHotkey: (event) => onSave(event),
    isHotKeyDisabled: isLoading,
    actionTitle: 'Save Filter',
  });

  const getDisabledStateExplanation = () => {
    if (isEmptyWhereClause(whereClause)) {
      return 'To create a custom filter, please add at least one filter.';
    }

    if (hasIncompleteFilters) {
      return 'Please complete all filters before creating a custom filter.';
    }

    if (!filterName) {
      return 'Please enter a name for this custom filter.';
    }

    return '';
  };

  const onDelete = async () => {
    try {
      if (!analysisID) {
        return;
      }

      setIsDeleteInProgress(true);

      await updateAnalysis({
        variables: {
          id: analysisID,
          editToken: analysisEditToken ?? '',
          status: Status.Archived,
          tenantID: tenantID,
        },
      });
      onClose();
      showSuccessNotification({
        contentComponent: `Custom Filter "${filterName}" deleted`,
        props: {
          icon: <FilterListRounded />,
        },
      });
      trackCustomFilterEvents({
        eventName: 'Custom Filter Deleted',
        filterName: filterName ?? '',
        eql: eql ?? '',
      });
    } catch {
    } finally {
      setIsDeleteInProgress(false);
    }
  };

  const isSaveDisabled =
    hasIncompleteFilters ||
    isEmptyWhereClause(whereClause) ||
    !filterName ||
    isDisabled;

  return (
    <Stack display="flex" direction="row" width="100%">
      {showDeleteButton && (
        <Stack justifySelf="flex-start">
          <LoadingButton
            variant="text"
            color="error"
            onClick={() => onDelete()}
            loading={isDeleteInProgress}
            disabled={isDisabled}
          >
            Delete filter
          </LoadingButton>
        </Stack>
      )}
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        spacing={1}
        justifySelf="flex-end"
        ml="auto"
      >
        <Button
          variant="outlined"
          color="primary"
          endIcon={<EscHotKeyIcon />}
          onClick={() => onClose()}
        >
          Cancel
        </Button>
        <Tooltip title={getDisabledStateExplanation()}>
          <Box>
            <LoadingButton
              loading={isUpdateInProgress || isCreateInProgress}
              disabled={isSaveDisabled}
              variant="contained"
              color="primary"
              onClick={(event) => onSave(event)}
              endIcon={
                !(isUpdateInProgress || isCreateInProgress) && (
                  <CmdHotKeyIcon
                    plusHotKeyNode={
                      <KeyboardReturnRounded
                        sx={{
                          fontSize: '1.1rem',
                        }}
                      />
                    }
                    backgroundColor={
                      isSaveDisabled ? 'misc.hotKey.disabled' : undefined
                    }
                  />
                )
              }
            >
              Save
            </LoadingButton>
          </Box>
        </Tooltip>
      </Stack>
    </Stack>
  );
};
